import { subject } from '@casl/ability';
import Clarity from '@microsoft/clarity';
import React, { useEffect } from 'react';
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
} from 'react-router-dom';

import './App.css';

import { SvgSprite } from './common/SvgSprite';
import { Spinner } from './components/Spinner';
import { UserRole, UserType, ACTIONS, Resource } from './constants/constants';
import { AbilityProvider } from './contexts/AbilityContext/AbilityProvider';
import { CanRoute } from './contexts/AbilityContext/components/CanRoute';
import { AppProvider } from './contexts/AppProvider';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { UserTeam } from './hooks/useUserTeam';
import Layout from './Layout';
import { AboutUs } from './pages/AboutUs';
import { AnalyticsPage } from './pages/Analytics';
import { CheckInContainer } from './pages/Check-In/Container';
import { DashboardPage } from './pages/Dashboard';
import Login from './pages/Login/containers';
// import { PerformanceReviewPage } from './pages/PerformanceReview'; // TODO: Uncomment when re-enabling Performance Review
import { SettingsPage } from './pages/Settings';
import { GoogleAuthCallback } from './pages/Settings/Integrations/callbacks/GoogleAuthCallback';
import SlackAuthCallback from './pages/Settings/Integrations/callbacks/SlackAuthCallback';
import UserProfile from './pages/UserProfile';
import { YourTeamsPage } from './pages/YourTeams';

export function getDefaultRoute(userData: UserTeam | null): string {
  if (!userData) return '/';

  if (
    userData.userType === UserType.External ||
    userData.role === UserRole.PARTNER
  ) {
    if (userData.accessibleTeams.length === 1) {
      return `/your-teams/${userData.accessibleTeams[0].id}`;
    }
    return '/dashboard';
  }
  if (userData.userType === UserType.Internal) {
    return '/checkin';
  }

  return '/';
}

function AppRoutes(): JSX.Element {
  const { user, userData, userDataLoading: isLoading } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (process.env.REACT_APP_CLARITY_PROJECT_ID) {
      Clarity.init(process.env.REACT_APP_CLARITY_PROJECT_ID);
      if (user) {
        Clarity.identify(
          user?.id || '',
          '',
          '',
          (user?.user_metadata?.name || '') as string,
        );
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spinner />
      </div>
    );
  }
  if (!userData) {
    return <Navigate to="/login" replace />;
  }
  return (
    <Layout>
      <Routes>
        <Route
          path="/checkin"
          element={
            <CanRoute
              action={ACTIONS.CREATE}
              subject={Resource.CheckIn}
              element={<CheckInContainer />}
            />
          }
        />
        <Route
          path="/settings/*"
          element={
            <CanRoute
              action={ACTIONS.READ}
              subject={Resource.Settings}
              element={<SettingsPage />}
            />
          }
        />
        <Route path="/profile" element={<UserProfile />} />
        <Route path="/auth/google/callback" element={<GoogleAuthCallback />} />
        <Route path="/auth/slack/callback" element={<SlackAuthCallback />} />
        <Route
          path="/dashboard"
          element={
            <CanRoute
              action={ACTIONS.READ}
              subject={Resource.Dashboard}
              element={<DashboardPage />}
            />
          }
        />
        <Route
          path="/your-teams/:teamId/*"
          element={
            <CanRoute
              action={ACTIONS.READ}
              subject={subject('YourTeams', {
                teamId: location.pathname.split('/')[2] || '',
              })}
              element={<YourTeamsPage />}
            />
          }
        />
        <Route
          path="/"
          element={<Navigate to={getDefaultRoute(userData)} replace />}
        />
        <Route
          path="/analytics/*"
          element={
            <CanRoute
              action={ACTIONS.READ}
              subject={Resource.Analytics}
              element={<AnalyticsPage />}
            />
          }
        />
        {/* TODO: Re-enable Performance Review route in a month */}
        {/* <Route
          path="/performance-review/*"
          element={
            <CanRoute
              action={ACTIONS.READ}
              subject={Resource.PerformanceReview}
              element={<PerformanceReviewPage />}
            />
          }
        /> */}
        <Route path="/*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Layout>
  );
}

function App(): JSX.Element {
  return (
    <>
      <SvgSprite />
      <AppProvider>
        <AuthProvider>
          <ToastProvider>
            <AbilityProvider>
              <Router>
                <div className="min-h-screen bg-gray-100">
                  <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/about" element={<AboutUs />} />
                    <Route path="/*" element={<AppRoutes />} />
                  </Routes>
                </div>
              </Router>
            </AbilityProvider>
          </ToastProvider>
        </AuthProvider>
      </AppProvider>
    </>
  );
}

export default App;
